import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrescriptionLensSupplyMode, type ProductType } from '@optiflow/types';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { fieldGroup, visibleDetailFields, type DetailFieldSpec } from '../variant-form';

interface VariantDetailFieldsProps {
  type: ProductType;
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
}

/**
 * Renders the fields that belong to a product type, in three groups: the main grid, the properties checkboxes, and a collapsed section of advanced prescription limits.
 *
 * Driven entirely by the field specs, so adding an optical attribute is a data change in `variant-form.ts` — the form does not grow another branch. Fields a type does not show keep their values in the form state and are still sent, so editing never clears them.
 */
export function VariantDetailFields({ type, values, errors, onChange }: VariantDetailFieldsProps) {
  const { t } = useTranslation();
  const fields = visibleDetailFields(type, values);

  const main = fields.filter((field) => fieldGroup(field) === 'main');
  const features = fields.filter((field) => fieldGroup(field) === 'features');
  const advanced = fields.filter((field) => fieldGroup(field) === 'advanced');

  // Collapsed by default; opened when one of its fields is refused so the error is never hidden.
  const advancedHasError = advanced.some((field) => Boolean(errors[field.key]));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useEffect(() => {
    if (advancedHasError) setAdvancedOpen(true);
  }, [advancedHasError]);

  if (fields.length === 0) {
    return <p className="text-muted text-sm">{t('catalog.variants.noTypeFields')}</p>;
  }

  const renderField = (field: DetailFieldSpec) => (
    <DetailField
      key={field.key}
      field={field}
      value={values[field.key] ?? ''}
      error={errors[field.key]}
      hint={
        field.key === 'supplyMode' && values.supplyMode === PrescriptionLensSupplyMode.MADE_TO_ORDER
          ? t('catalog.supplyModeHint.MADE_TO_ORDER')
          : undefined
      }
      onChange={(value) => onChange(field.key, value)}
    />
  );

  return (
    <div className="space-y-4">
      {main.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">{main.map(renderField)}</div>
      ) : null}

      {features.length > 0 ? (
        <fieldset className="border-line rounded-lg border p-3">
          <legend className="text-muted px-1 text-xs font-medium">
            {t('catalog.variants.features')}
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {features.map((field) => (
              <label key={field.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-brand-600 size-4"
                  checked={values[field.key] === 'true'}
                  onChange={(event) => onChange(field.key, String(event.target.checked))}
                />
                {t(field.labelKey)}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {advanced.length > 0 ? (
        <details
          className="border-line rounded-lg border"
          open={advancedOpen}
          onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
        >
          <summary className="text-ink cursor-pointer px-3 py-2.5 text-sm font-medium">
            {t('catalog.variants.advancedLimits')}
            <span className="text-muted ms-2 text-xs font-normal">
              {t('catalog.variants.advancedLimitsHint')}
            </span>
          </summary>
          <div className="border-line grid gap-4 border-t p-3 sm:grid-cols-2">
            {advanced.map(renderField)}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function DetailField({
  field,
  value,
  error,
  hint,
  onChange,
}: {
  field: DetailFieldSpec;
  value: string;
  error?: string;
  hint?: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const label = t(field.labelKey);

  if (field.kind === 'enum') {
    const options = [
      ...(field.noEmptyOption ? [] : [{ value: '', label: t('catalog.fields.notSpecified') }]),
      ...(field.options ?? []).map((option) => ({
        value: option,
        label: t(`${field.optionLabelPrefix ?? ''}.${option}`),
      })),
    ];

    return (
      <FormField label={label} error={error} hint={hint} required={field.required}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            options={options}
            value={value}
            hasError={Boolean(error)}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </FormField>
    );
  }

  return (
    <Input
      label={label}
      error={error}
      hint={hint}
      required={field.required}
      // Numbers stay LTR so a minus sign reads correctly in an Arabic layout.
      dir={field.kind === 'text' ? undefined : 'ltr'}
      type={field.kind === 'text' ? 'text' : 'number'}
      inputMode={
        field.kind === 'integer' ? 'numeric' : field.kind === 'decimal' ? 'decimal' : undefined
      }
      step={
        field.kind === 'decimal'
          ? field.quarterStep
            ? 0.25
            : 0.01
          : field.kind === 'integer'
            ? 1
            : undefined
      }
      min={field.min}
      max={field.max}
      placeholder={field.placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
